import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Flatpickr from 'react-flatpickr';

import { Card, CardBody, Col, Form, Label, Row, CardFooter, Input, Container, Offcanvas, OffcanvasHeader, OffcanvasBody } from 'reactstrap';
// import { PROGRAM_USER_EMPTY } from '../../../../../store/application/actionType'
import { PreviewCardHeader } from '../../../../../../Common';
import {
  GET_CATEGORY_EMPTY,
  GET_COUNTRY,
  GET_LOCATION_EMPTY,
  GET_PARTNER_EMPTY,
  GET_REGION,
  GET_REGION_EMPTY,
  GET_SUB_REGION,
  GET_SUB_REGION_EMPTY,
} from '../../../../../../../store/application/actionType';
import { AsyncPaginate } from 'react-select-async-paginate';
import { customHeaders, uaeProjectsBaseURL } from '../../../../../../Config';
import { Constant } from '../../../../../../Helpers/constant';
import Select from 'react-select';
import { isValidAplha, isValidNumber } from '../../../../../../Helpers/Helpers';

const CustomFilter = props => {
  const dispatch = useDispatch();

  /* ---------------------------- LOCAL STATES ---------------------------- */
  const initialState = {
    name: ``,
    merchantName: ``,
    phoneNumber: ``,
    email: ``,
    updateDate: ``,
    categoryId: null,
    partnerId: null,
    countryId: null,
    regionId: null,
    subRegionId: null,
    airportId: null,
  };
  const [filter, setFilter] = useState(initialState);
  const [isFilterDisabled, setIsFilterDisabled] = useState(false);
  const [isResetDisabled, setIsResetDisabled] = useState(true);
  const [allCountries, setAllCountries] = useState([]);
  const [allRegions, setAllRegions] = useState(null);
  const [allSubRegions, setAllSubRegions] = useState(null);

  /* ---------------------------- REDUX STATES ---------------------------- */
  // Countries
  const { countries } = useSelector(state => ({
    countries: state?.Countries?.data,
  }));

  // Regions
  const { regions } = useSelector(state => ({
    regions: state?.Regions?.data,
  }));

  // Sub Regions
  const { subRegions } = useSelector(state => ({
    subRegions: state?.SubRegions?.data,
  }));

  useEffect(() => {
    if (props?.appliedFilters !== null) {
      setFilter(props?.appliedFilters);
      validationCheck(props?.appliedFilters);
    }
  }, [props?.appliedFilters]);

  useEffect(() => {
    if (countries === null) {
      dispatch({
        type: GET_COUNTRY,
      });
    }
  }, [dispatch]);

  useEffect(() => {
    if (countries !== null) {
      setAllCountries(
        countries?.map(country => {
          return {
            value: country?.countryId,
            label: country?.name,
            data: country,
          };
        }),
      );
    } else {
      setAllCountries(null);
    }
  }, [countries]);

  useEffect(() => {
    if (regions !== null) {
      setAllRegions(
        regions?.map(region => {
          return {
            value: region?.stateId,
            label: region?.name,
          };
        }),
      );
    } else {
      setAllRegions(null);
    }
  }, [regions]);

  useEffect(() => {
    if (subRegions !== null) {
      setAllSubRegions(
        subRegions?.map(subRegion => {
          return {
            value: subRegion?.cityId,
            label: subRegion?.name,
          };
        }),
      );
    } else {
      setAllSubRegions(null);
    }
  }, [subRegions]);

  /* ---------------------------- INPUT CHANGE HANDLER ---------------------------- */
  const inputChangeHandler = e => {
    const { name, value } = e.target;
    let updatedState = { ...filter };

    switch (name) {
      case 'name':
        updatedState = {
          ...filter,
          name: isValidAplha(value) ? value : filter?.name,
        };
        break;

      case 'phoneNumber':
        updatedState = {
          ...filter,
          phoneNumber: isValidNumber(value) ? value : filter?.phoneNumber,
        };
        break;

      case 'email':
        updatedState = {
          ...filter,
          email: value,
        };
        break;

      case 'merchantName':
        updatedState = {
          ...filter,
          merchantName: isValidAplha(value) ? value : filter?.merchantName,
        };
        break;

      default:
      // do nothing
    }

    setFilter(updatedState);
    validationCheck({ ...updatedState });
  };

  /* ---------------------------- INPUT SELECT HANDLER ---------------------------- */
  const inputSelectHandler = (label, data) => {
    let updatedState = { ...filter };

    switch (label) {
      case 'country':
        updatedState = {
          ...filter,
          countryId: data,
          regionId: null,
          subRegionId: null,
        };

        dispatch({ type: GET_SUB_REGION_EMPTY });

        dispatch({
          type: GET_REGION,
          payload: {
            urlParam: {
              countryId: parseInt(data?.value),
            },
          },
        });

        dispatch({
          type: GET_SUB_REGION,
          payload: {
            urlParam: {
              countryId: parseInt(data?.value),
            },
          },
        });
        break;

      case 'regionId':
        updatedState = {
          ...filter,
          regionId: data,
          subRegionId: null,
        };

        dispatch({
          type: GET_SUB_REGION,
          payload: {
            urlParam: {
              regionId: parseInt(data?.value),
            },
          },
        });
        break;

      case 'subRegionId':
        updatedState = {
          ...filter,
          subRegionId: data,
        };
        break;

      default:
      // do nothing
    }

    setFilter(updatedState);
    validationCheck({ ...updatedState });
  };

  /* ---------------------------- DATE SELECT HANDLER ---------------------------- */
  const datePickHandler = (flag, date) => {
    let updatedState = { ...filter };

    switch (flag) {
      case 'updateDate':
        updatedState = {
          ...filter,
          updateDate: new Date(date).toISOString(),
        };
        break;

      default:
      // do nothing
    }

    setFilter(updatedState);
    validationCheck(updatedState);
  };

  /* ---------------------------- TYPED CATEGORIES HANDLER ---------------------------- */
  async function loadCategoriesOptions(search, loadedOptions) {
    const response = await fetch(`${uaeProjectsBaseURL}/admin/categories?name=${search}`, {
      method: `GET`,
      headers: new Headers({
        Authorization: Constant?.CLIENT_TOKEN,
        ...customHeaders,
      }),
    });
    const categoriesResponse = await response.json();

    return {
      options: categoriesResponse?.results?.map((data, index) => {
        return {
          value: data?.objectId,
          label: data.name,
          completeData: data,
        };
      }),
      hasMore: false,
    };
  }

  const onSelectCategory = event => {
    let updatedState = { ...filter, categoryId: event, partnerId: null };
    setFilter(updatedState);
    validationCheck(updatedState);
  };

  /* ---------------------------- TYPED PARTNERS HANDLER ---------------------------- */
  async function loadPartnersOptions(search, loadedOptions) {
    const dynamicParams = {
      name: search,
      categoryId: filter?.categoryId === null ? null : filter?.categoryId?.value,
    };

    const queryString = Object.keys(dynamicParams)
      .filter(key => dynamicParams[key] !== null && dynamicParams[key] !== undefined)
      .map(key => encodeURIComponent(key) + '=' + encodeURIComponent(dynamicParams[key]))
      .join('&');

    const baseURL = `${uaeProjectsBaseURL}/admin/partners`;
    const completeURL = queryString ? `${baseURL}?${queryString}` : baseURL;

    const response = await fetch(`${completeURL}`, {
      method: `GET`,
      headers: new Headers({
        Authorization: Constant?.CLIENT_TOKEN,
        ...customHeaders,
      }),
    });
    const categoriesResponse = await response.json();

    return {
      options: categoriesResponse?.results?.map((data, index) => {
        return {
          value: data?.objectId,
          label: data?.name,
          completeData: data,
        };
      }),
      hasMore: false,
    };
  }

  const onSelectPartner = event => {
    let updatedState = { ...filter, partnerId: event };
    setFilter(updatedState);
    validationCheck(updatedState);
  };

  /* ---------------------------- VALIDATION CHECK ---------------------------- */
  const validationCheck = data => {
    if (
      data?.name !== `` ||
      data?.merchantName !== `` ||
      data?.phoneNumber !== `` ||
      data?.email !== `` ||
      data?.updateDate !== `` ||
      data?.categoryId !== null ||
      data?.partnerId !== null ||
      data?.countryId !== null ||
      data?.regionId !== null ||
      data?.subRegionId !== null
    ) {
      setIsResetDisabled(false);
    } else {
      setIsResetDisabled(true);
    }
  };

  /* ---------------------------- ON SUBMIT HANDLER ---------------------------- */
  const filterSubmitHandler = () => {
    props.filterHandler({ ...filter });
  };

  /* ---------------------------- ON RESET HANDLER ---------------------------- */
  const resetFilterHandler = () => {
    setFilter({ ...initialState });
    setIsResetDisabled(true);

    dispatch({
      type: GET_CATEGORY_EMPTY,
    });

    dispatch({
      type: GET_PARTNER_EMPTY,
    });

    dispatch({
      type: GET_LOCATION_EMPTY,
    });

    dispatch({
      type: GET_REGION_EMPTY,
    });

    dispatch({
      type: GET_SUB_REGION_EMPTY,
    });
  };

  return (
    <>
      <Offcanvas direction="end" isOpen={props.show} id="offcanvasExample" toggle={() => props.onCloseClick()}>
        <OffcanvasHeader className="bg-light" toggle={() => props.onCloseClick()}>
          Outlet Filters
        </OffcanvasHeader>
        <OffcanvasBody>
          <Row>
            <Col md={12}>
              <div className="mb-3">
                <Label htmlFor="name" className="form-label">
                  Display Name
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  className="form-control"
                  value={filter?.name}
                  onChange={inputChangeHandler}
                  autoComplete="off"
                />
              </div>
            </Col>

            <Col md={12}>
              <div className="mb-3">
                <Label htmlFor="phoneNumber" className="form-label">
                  Phone No.
                </Label>
                <Input
                  id="phoneNumber"
                  name="phoneNumber"
                  type="text"
                  className="form-control"
                  value={filter?.phoneNumber}
                  onChange={inputChangeHandler}
                  autoComplete="off"
                />
              </div>
            </Col>

            <Col md={12}>
              <div className="mb-3">
                <Label htmlFor="email" className="form-label">
                  Email
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="text"
                  className="form-control"
                  value={filter?.email}
                  onChange={inputChangeHandler}
                  autoComplete="off"
                />
              </div>
            </Col>

            <Col md={12}>
              <div className="mb-3">
                <Label htmlFor="merchantName" className="form-label">
                  Merchant Name
                </Label>
                <Input
                  id="merchantName"
                  name="merchantName"
                  type="text"
                  className="form-control"
                  value={filter?.merchantName}
                  onChange={inputChangeHandler}
                  autoComplete="off"
                />
              </div>
            </Col>

            <Col md={12}>
              <div className="mb-3">
                <Label htmlFor="name" className="form-label">
                  Sub-Category
                </Label>
                <AsyncPaginate
                  debounceTimeout={500}
                  value={filter?.categoryId}
                  loadOptions={loadCategoriesOptions}
                  onChange={e => onSelectCategory(e)}
                  isMulti={false}
                  closeMenuOnSelect={true}
                  noOptionsMessage={() => 'No results found'}
                  cacheUniqs={[['code']]}
                  placeholder="Enter sub-category..."
                />
              </div>
            </Col>

            <Col md={12}>
              <div className="mb-3">
                <Label htmlFor="name" className="form-label">
                  Brand
                </Label>
                <AsyncPaginate
                  debounceTimeout={500}
                  value={filter?.partnerId}
                  loadOptions={loadPartnersOptions}
                  onChange={e => onSelectPartner(e)}
                  isMulti={false}
                  closeMenuOnSelect={true}
                  noOptionsMessage={() => 'No results found'}
                  cacheUniqs={[['code']]}
                  placeholder="Enter partner..."
                />
              </div>
            </Col>

            <Col md={12}>
              <div className="mb-3">
                <Label htmlFor="name" className="form-label">
                  Country
                </Label>
                <Select
                  id="country"
                  name="country"
                  options={allCountries ?? []}
                  value={filter?.countryId}
                  onChange={data => inputSelectHandler(`country`, data)}
                />
              </div>
            </Col>

            <Col md={12}>
              <div className="mb-3">
                <Label htmlFor="name" className="form-label">
                  Region
                </Label>
                <Select
                  id="regionId"
                  name="regionId"
                  options={allRegions ?? []}
                  value={filter?.regionId}
                  onChange={data => inputSelectHandler(`regionId`, data)}
                />
              </div>
            </Col>

            <Col md={12}>
              <div className="mb-3">
                <Label htmlFor="name" className="form-label">
                  Sub Region
                </Label>
                <Select
                  id="subRegionId"
                  name="subRegionId"
                  options={allSubRegions ?? []}
                  value={filter?.subRegionId}
                  onChange={data => inputSelectHandler(`subRegionId`, data)}
                />
              </div>
            </Col>

            <Col md={12}>
              <div className="mb-3">
                <Label htmlFor="to" className="form-label">
                  Last Updated At
                  <span className="icon_calendar">
                    <i className="las la-calendar"></i>
                  </span>
                </Label>
                <div className="area_expandable">
                  <Flatpickr
                    className="form-control"
                    name="to"
                    id="to"
                    value={filter?.updateDate}
                    onChange={date => datePickHandler(`updateDate`, date)}
                    options={{
                      enableTime: false,
                      dateFormat: 'd M, Y',
                      maxDate: 'today',
                    }}
                  />
                </div>
              </div>
            </Col>
          </Row>
        </OffcanvasBody>

        <div className="offcanvas-footer border-top p-3 text-center hstack gap-2">
          <button
            className="btn btn-light w-100"
            onClick={resetFilterHandler}
            // disabled={isResetDisabled}
          >
            Reset Filter
          </button>

          <button className="btn btn-primary w-100" onClick={filterSubmitHandler}>
            Search
          </button>
        </div>
      </Offcanvas>
    </>
  );
};

export default CustomFilter;
